import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { useAdminMe } from '../components/RequireAdmin'
import { ApiError, type Limits } from '../lib/api'
import { adminApi, can, type AiSettingsStatus } from '../lib/adminApi'

/**
 * 관리자 설정 — AI API 키 입력·모델 선택(사용자 결정 08-24) · 갯수 상한(사용자 지시 08-25).
 *
 * env(SOPS)는 **디폴트**이고 여기 저장한 값이 우선한다(서버 admin-ai.ts).
 * **저장된 키는 다시 보여주지 않는다** — 서버가 원문을 어떤 응답에도 싣지 않으므로
 * 이 화면도 "설정돼 있음 · 어디서 온 값" 까지만 안다. 바꾸려면 새로 입력한다.
 * 읽기는 로그인이면 되고, 변경 버튼은 admin:manage만 그린다(서버가 다시 막는다).
 */

const SOURCE_LABEL: Record<'admin' | 'env', string> = {
  admin: '관리자 설정값',
  env: '서버 기본값(env)',
}

function sourceLabel(source: 'admin' | 'env' | null): string {
  return source === null ? '설정 안 됨' : SOURCE_LABEL[source]
}

function errorMessage(error: unknown): string | null {
  if (error === null || error === undefined) return null
  if (error instanceof ApiError) {
    if (error.status === 403) return '이 작업을 할 권한이 없습니다.'
    if (error.code === 'pii_key_missing')
      return '서버에 암호화 키가 없어 API 키를 저장할 수 없습니다. 구축 담당자에게 문의하세요.'
    return error.detail[0] ?? error.code
  }
  return '처리 중 오류가 발생했습니다.'
}

export function AdminSettingsPage() {
  const me = useAdminMe()
  const mayManage = can(me.data, 'admin', 'manage')

  const status = useQuery({ queryKey: ['ai-settings'], queryFn: () => adminApi.aiSettings() })

  return (
    <div>
      <div className="admin-bar">
        <div>
          <h1 className="page-title">설정</h1>
          <p className="page-desc">
            AI 입력보조가 쓰는 API 키·모델과 등록 화면의 갯수 상한을 관리합니다. 여기서
            저장한 값이 서버 기본값(env·코드 초기값)보다 우선합니다.
          </p>
        </div>
      </div>

      <QueryState isPending={status.isPending} error={status.error} isEmpty={false}>
        {status.data !== undefined && <AiSettingsSection status={status.data} mayManage={mayManage} />}
      </QueryState>

      <LimitsSection mayManage={mayManage} />

      <section className="card-block">
        <h2>코드·라벨</h2>
        <p className="muted small">
          등록 화면 선택지(분야·주 대상·자원 …)의 이름과 순서를 바꿉니다. 코드값은 그대로라
          이미 저장된 카드에는 영향이 없습니다.
        </p>
        <Link className="btn ghost" to="/admin/settings/codes">
          코드·라벨 편집
        </Link>
      </section>
    </div>
  )
}

/**
 * 갯수 상한(030) — 코드에 박혀 있던 숫자를 여기로 옮겼다(사용자 지시 08-25).
 *
 * 세 값은 **대외 공표·회의로 바뀐다**(해결카드 3은 08-07 웹자보, 분야 2는 08-21 확정).
 * 바꿀 때마다 배포하지 않기 위해 설정이 됐다. 저장은 세 값을 한 번에 보낸다 —
 * 부분 저장을 받지 않으므로 손대지 않은 칸도 현재값 그대로 실린다.
 */
function LimitsSection(props: { mayManage: boolean }) {
  const { mayManage } = props
  const queryClient = useQueryClient()
  const limits = useQuery({ queryKey: ['admin-limits'], queryFn: () => adminApi.limits() })
  // null = 아직 손 안 댐. 저장 후 초기화해 서버가 준 값을 다시 그린다.
  const [draft, setDraft] = useState<Limits | null>(null)

  const save = useMutation({
    mutationFn: (body: Limits) => adminApi.setLimits(body),
    onSuccess: () => {
      setDraft(null)
      void queryClient.invalidateQueries({ queryKey: ['admin-limits'] })
      // 공개 화면이 쓰는 캐시도 함께 비운다 — 안 그러면 관리자가 바꾼 값이
      // 같은 탭의 등록 화면에 최대 10분 늦게 닿는다(codes.ts staleTime).
      void queryClient.invalidateQueries({ queryKey: ['settings-limits'] })
    },
  })

  const current = draft ?? limits.data?.limits ?? null
  const max = limits.data?.max ?? 20
  const changed =
    current !== null &&
    limits.data !== undefined &&
    (['categoryMax', 'resourceSuggestMax', 'solutionCardPerOrg'] as const).some(
      (k) => current[k] !== limits.data.limits[k],
    )

  const setField = (key: keyof Limits, raw: string): void => {
    if (current === null) return
    const n = Number(raw)
    // 빈 칸·비정수는 상태에 담지 않는다 — NaN을 담으면 저장 버튼이 조용히 잘못된 값을 보낸다.
    if (!Number.isInteger(n) || n < 1 || n > max) return
    setDraft({ ...current, [key]: n })
  }

  const FIELDS: { key: keyof Limits; label: string; hint: string }[] = [
    { key: 'categoryMax', label: '시민문제 분야 선택 수', hint: '등록 화면에서 고를 수 있는 분야 개수입니다.' },
    { key: 'resourceSuggestMax', label: 'AI 필요자원 제안 수', hint: 'AI 입력보조가 한 번에 제안하는 자원 개수입니다.' },
    { key: 'solutionCardPerOrg', label: '기업당 해결카드 수', hint: '기업 하나가 등록할 수 있는 해결카드 개수입니다. 이미 넘긴 기업의 기존 카드 수정은 막지 않습니다.' },
  ]

  return (
    <section className="card-block">
      <h2>갯수 상한</h2>
      <QueryState isPending={limits.isPending} error={limits.error} isEmpty={false}>
        {current !== null && (
          <>
            {FIELDS.map((f) => (
              <div className="form-row" key={f.key}>
                <label htmlFor={`limit-${f.key}`}>{f.label}</label>
                <input
                  id={`limit-${f.key}`}
                  type="number"
                  min={1}
                  max={max}
                  value={current[f.key]}
                  disabled={!mayManage}
                  onChange={(e) => setField(f.key, e.target.value)}
                />
                <p className="muted small">{f.hint}</p>
              </div>
            ))}

            {mayManage ? (
              <div className="form-actions">
                <button
                  type="button"
                  className="btn"
                  disabled={!changed || save.isPending}
                  onClick={() => save.mutate(current)}
                >
                  저장
                </button>
              </div>
            ) : (
              <p className="muted small">설정 변경은 관리 권한(admin:manage) 계정만 할 수 있습니다.</p>
            )}

            {save.error !== null && (
              <p className="error" role="alert">
                {errorMessage(save.error)}
              </p>
            )}
          </>
        )}
      </QueryState>
    </section>
  )
}

function AiSettingsSection(props: { status: AiSettingsStatus; mayManage: boolean }) {
  const { status, mayManage } = props
  const queryClient = useQueryClient()
  const [keyInput, setKeyInput] = useState('')
  const [modelInput, setModelInput] = useState<string | null>(null) // null = 아직 손 안 댐

  // 모델 목록은 유효 키가 있어야 온다(admin:manage 전용) — 실패하면 직접 입력으로 폴백한다.
  const models = useQuery({
    queryKey: ['ai-models'],
    queryFn: () => adminApi.aiModels(),
    enabled: mayManage && status.keyConfigured,
    retry: false,
  })

  const save = useMutation({
    mutationFn: (body: { apiKey?: string | null; model?: string | null }) =>
      adminApi.putAiSettings(body),
    onSuccess: () => {
      setKeyInput('')
      setModelInput(null)
      void queryClient.invalidateQueries({ queryKey: ['ai-settings'] })
      void queryClient.invalidateQueries({ queryKey: ['ai-models'] })
    },
  })

  const test = useMutation({ mutationFn: () => adminApi.aiTest() })

  const shownModel = modelInput ?? status.model ?? ''
  const modelChanged = modelInput !== null && modelInput !== (status.model ?? '')
  const canSave = !save.isPending && (keyInput.trim().length > 0 || modelChanged)

  const submit = (): void => {
    const body: { apiKey?: string; model?: string } = {}
    if (keyInput.trim().length > 0) body.apiKey = keyInput.trim()
    if (modelChanged && shownModel.length > 0) body.model = shownModel
    if (body.apiKey === undefined && body.model === undefined) return
    save.mutate(body)
  }

  return (
    <section className="card-block">
      <h2>AI 입력보조</h2>
      <p className={status.enabled ? 'ok' : 'muted'}>
        {status.enabled
          ? 'AI 입력보조가 켜져 있습니다.'
          : 'AI 입력보조가 꺼져 있습니다 — 키와 모델이 모두 있어야 켜집니다. 꺼져 있어도 등록은 됩니다.'}
      </p>

      <dl className="detail-list">
        <dt>API 키</dt>
        <dd>
          {status.keyConfigured ? `설정됨 · ${sourceLabel(status.keySource)}` : '설정 안 됨'}
          {status.keySource === 'admin' && status.envDefault.keyConfigured && (
            <span className="muted small"> (되돌리면 서버 기본값 키로 동작)</span>
          )}
        </dd>
        <dt>모델</dt>
        <dd>
          {status.model !== null ? `${status.model} · ${sourceLabel(status.modelSource)}` : '설정 안 됨'}
        </dd>
      </dl>

      {mayManage ? (
        <>
          <div className="form-row">
            <label htmlFor="ai-key">새 API 키</label>
            <input
              id="ai-key"
              type="password"
              autoComplete="off"
              placeholder={status.keyConfigured ? '저장된 키는 표시되지 않습니다 — 바꿀 때만 입력' : 'API 키 입력'}
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
            />
            {status.keySource === 'admin' && (
              <button
                type="button"
                className="btn ghost small-btn"
                disabled={save.isPending}
                onClick={() => save.mutate({ apiKey: null })}
              >
                키를 서버 기본값으로 되돌리기
              </button>
            )}
          </div>

          <div className="form-row">
            <label htmlFor="ai-model">모델</label>
            {models.data !== undefined ? (
              <select
                id="ai-model"
                value={shownModel}
                onChange={(e) => setModelInput(e.target.value)}
              >
                {shownModel === '' && <option value="">모델 선택</option>}
                {models.data.models.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            ) : (
              // 목록을 못 받는 상태(키 미설정·조회 실패)에서도 모델은 정할 수 있어야 한다.
              <input
                id="ai-model"
                type="text"
                placeholder="모델명 직접 입력 (예: 날짜 핀 ID)"
                value={shownModel}
                onChange={(e) => setModelInput(e.target.value)}
              />
            )}
            {status.modelSource === 'admin' && (
              <button
                type="button"
                className="btn ghost small-btn"
                disabled={save.isPending}
                onClick={() => save.mutate({ model: null })}
              >
                모델을 서버 기본값으로 되돌리기
              </button>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn" disabled={!canSave} onClick={submit}>
              저장
            </button>
            <button
              type="button"
              className="btn ghost"
              disabled={test.isPending || !status.enabled}
              onClick={() => test.mutate()}
            >
              연결 테스트
            </button>
          </div>

          {save.error !== null && (
            <p className="error" role="alert">
              {errorMessage(save.error)}
            </p>
          )}
          {test.data !== undefined && (
            <p className={test.data.ok ? 'ok' : 'error'} role="status">
              {test.data.ok
                ? test.data.modelAvailable === false
                  ? '연결은 됐지만 설정한 모델이 계정 목록에 없습니다.'
                  : '연결 성공 — 키와 모델이 유효합니다.'
                : `연결 실패 (${test.data.reason ?? '원인 미상'})`}
            </p>
          )}
          {test.error !== null && (
            <p className="error" role="alert">
              {errorMessage(test.error)}
            </p>
          )}
        </>
      ) : (
        <p className="muted small">설정 변경은 관리 권한(admin:manage) 계정만 할 수 있습니다.</p>
      )}
    </section>
  )
}
